import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  TextField,
  Autocomplete,
  Chip,
  Button,
  Divider,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { api } from "../../utils/api";

const FREQUENCIES = [
  { value: "one_time",    label: "One Time" },
  { value: "monthly",     label: "Monthly" },
  { value: "quarterly",   label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "annual",      label: "Annual" },
];

const FALLBACK_CATEGORIES = [
  "Accounting", "Audit", "Book Keeping", "Business Setup", "Certification",
  "Company Compliance", "Corporate Law", "Due Diligence", "Financial Reporting",
  "General", "GST", "Income Tax", "Internal Audit", "Legal Drafting",
  "Licenses & Registrations", "Payroll", "ROC / MCA", "Risk Advisory",
  "Secretarial Services", "Statutory Audit", "TDS", "Taxation", "Valuation",
];

const SORT_OPTIONS = [
  { value: "id",            label: "Newest First" },
  { value: "name",          label: "Name" },
  { value: "default_price", label: "Price" },
  { value: "sla_days",      label: "SLA Days" },
  { value: "created_at",    label: "Created Date" },
  { value: "service_type",  label: "Category" },
];

export const emptyFilters = {
  search: "",
  frequency: [],
  category: [],
  audit: "",
  status: "",
  price_min: "",
  price_max: "",
  sla_min: "",
  sla_max: "",
  has_checklist: "",
  sort_by: "id",
  sort_order: "desc",
};

export default function ServiceFilterDrawer({ open, onClose, filters, onApply }) {
  const [local, setLocal] = useState(filters || emptyFilters);
  const [options, setOptions] = useState({ categories: FALLBACK_CATEGORIES });

  useEffect(() => {
    if (open) setLocal(filters || emptyFilters);
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/services/filter_options.php");
        if (res.categories && res.categories.length > 0) {
          setOptions({ categories: res.categories });
        }
      } catch (e) {
        // non-fatal
      }
    })();
  }, [open]);

  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));

  const handleReset = () => setLocal(emptyFilters);

  const handleApply = () => {
    onApply(local);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <FilterAltIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Filter Services
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              BASIC
            </Typography>

            <TextField
              label="Service Name"
              size="small"
              value={local.search}
              onChange={(e) => set("search", e.target.value)}
              fullWidth
            />

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              CATEGORY
            </Typography>

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={options.categories}
              value={local.category}
              onChange={(_e, v) => set("category", v)}
              renderInput={(p) => <TextField {...p} label="Category" />}
              renderTags={(val, getTagProps) =>
                val.map((opt, i) => (
                  <Chip label={opt} size="small" {...getTagProps({ index: i })} />
                ))
              }
            />

            <Autocomplete
              multiple
              size="small"
              options={FREQUENCIES}
              value={FREQUENCIES.filter((f) => local.frequency.includes(f.value))}
              onChange={(_e, v) => set("frequency", v.map((x) => x.value))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(p) => <TextField {...p} label="Frequency" />}
              renderTags={(val, getTagProps) =>
                val.map((opt, i) => (
                  <Chip label={opt.label} size="small" {...getTagProps({ index: i })} />
                ))
              }
            />

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              PRICING
            </Typography>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Min Price"
                type="number"
                size="small"
                value={local.price_min}
                onChange={(e) => set("price_min", e.target.value)}
                fullWidth
              />
              <TextField
                label="Max Price"
                type="number"
                size="small"
                value={local.price_max}
                onChange={(e) => set("price_max", e.target.value)}
                fullWidth
              />
            </Stack>

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              SLA
            </Typography>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Min SLA (days)"
                type="number"
                size="small"
                value={local.sla_min}
                onChange={(e) => set("sla_min", e.target.value)}
                fullWidth
              />
              <TextField
                label="Max SLA (days)"
                type="number"
                size="small"
                value={local.sla_max}
                onChange={(e) => set("sla_max", e.target.value)}
                fullWidth
              />
            </Stack>

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              FLAGS
            </Typography>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Audit Required</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={local.audit}
                onChange={(_e, v) => set("audit", v === null ? "" : v)}
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="1">Yes</ToggleButton>
                <ToggleButton value="0">No</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Status</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={local.status}
                onChange={(_e, v) => set("status", v === null ? "" : v)}
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="1">Active</ToggleButton>
                <ToggleButton value="0">Inactive</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>Has Checklist</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={local.has_checklist}
                onChange={(_e, v) => set("has_checklist", v || "")}
                fullWidth
              >
                <ToggleButton value="">Any</ToggleButton>
                <ToggleButton value="yes">Yes</ToggleButton>
                <ToggleButton value="no">No</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              SORT
            </Typography>

            <Stack direction="row" spacing={1}>
              <TextField
                select
                label="Sort By"
                size="small"
                value={local.sort_by}
                onChange={(e) => set("sort_by", e.target.value)}
                fullWidth
              >
                {SORT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Order"
                size="small"
                value={local.sort_order}
                onChange={(e) => set("sort_order", e.target.value)}
                sx={{ minWidth: 110 }}
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button variant="contained" fullWidth onClick={handleApply}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
